import {
  ALLERGEN_ICONS, AllergenIcon, Badge, Button, Card, DataTable, ErrorState, Field, Input, Notice, PageHeader, Row,
  Select, Stack, Textarea, fetchAllPages, useApi, useClampedPage, useI18n,
  type AllergenKind, type AllergenResponse, type Column, type UnmatchedAllergenResponse,
} from '@daghfal/core';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

const KINDS: AllergenKind[] = ['Food', 'Condition', 'Medicine', 'Environment'];

/** What the editor is open on: an entry, or a new one with the names it starts from. */
type Editing = { allergen: AllergenResponse } | { names: string[] } | null;

/** One name per line, or several on a line separated by commas. */
const splitNames = (text: string) => text.split(/[\n,،]/).map((name) => name.trim()).filter(Boolean);

/**
 * The allergen catalogue (Q75–Q77): the names an allergy is recognised by in every nursery, and the picture shown
 * beside it.
 *
 * A child's allergy keeps the words the nursery wrote and is matched to this list by name whenever it is read, so
 * each change here reaches every child already written that way at once, in every nursery. The work mostly starts
 * from the names nobody recognises: several spellings that mean one thing ("فاصولياء", "فاصولية") are gathered onto
 * one entry in one step.
 */
export function Allergens() {
  const api = useApi();
  const { t, formatNumber } = useI18n();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing>(null);

  const catalogue = useQuery({
    queryKey: ['platform', 'allergens', 'page', { search, page }],
    queryFn: () => api.platform.listAllergens({ search: search.trim() || undefined, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, catalogue.data?.totalPages);

  const columns: Column<AllergenResponse>[] = [
    {
      key: 'icon', header: t('allergens.column.icon'),
      render: (a) => <AllergenIcon icon={a.icon} allergen={a.nameAr} />,
    },
    { key: 'nameAr', header: t('allergens.column.nameAr'), render: (a) => <strong>{a.nameAr}</strong> },
    { key: 'nameEn', header: t('allergens.column.nameEn'), render: (a) => <span dir="ltr">{a.nameEn}</span> },
    { key: 'kind', header: t('allergens.column.kind'), render: (a) => t(`allergens.kind.${a.kind}`) },
    { key: 'names', header: t('allergens.column.names'), numeric: true, render: (a) => formatNumber(a.aliases.length) },
    { key: 'state', header: t('allergens.column.state'), render: (a) => <StateBadge allergen={a} /> },
    {
      key: 'actions', header: '',
      render: (a) => (
        <Button variant="secondary" onClick={() => setEditing({ allergen: a })}>{t('allergens.edit')}</Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('allergens.title')}
        lede={t('allergens.lede')}
        actions={<Button onClick={() => setEditing({ names: [] })}>{t('allergens.new')}</Button>}
      />

      {editing && (
        <AllergenEditor
          key={'allergen' in editing ? editing.allergen.id : editing.names.join('|')}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={(allergen) => setEditing({ allergen })}
        />
      )}

      <UnmatchedCard onCreate={(names) => setEditing({ names })} />

      <Card title={t('allergens.catalogue')}>
        <Stack>
          <Field label={t('allergens.search')}>
            {(props) => (
              <Input {...props} type="search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            )}
          </Field>
          <DataTable
            columns={columns}
            rows={catalogue.data?.items}
            rowKey={(a) => a.id}
            caption={t('allergens.catalogue')}
            isLoading={catalogue.isPending}
            error={catalogue.error}
            onRetry={() => void catalogue.refetch()}
          />
          {catalogue.data && catalogue.data.totalPages > 1 && (
            <Row>
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('action.previous')}</Button>
              <span>{formatNumber(page)} / {formatNumber(catalogue.data.totalPages)}</span>
              <Button variant="ghost" disabled={page >= catalogue.data.totalPages} onClick={() => setPage(page + 1)}>
                {t('action.next')}
              </Button>
            </Row>
          )}
        </Stack>
      </Card>
    </>
  );
}

function StateBadge({ allergen }: { allergen: AllergenResponse }) {
  const { t } = useI18n();
  if (allergen.mergedIntoId != null) return <Badge tone="neutral">{t('allergens.state.merged')}</Badge>;
  if (allergen.retiredAt) return <Badge tone="warning">{t('allergens.state.retired')}</Badge>;
  return <Badge tone="success">{t('allergens.state.active')}</Badge>;
}

/** Every entry that can still take names, for the pickers: the catalogue is small, so it is read whole. */
function useLiveAllergens() {
  const api = useApi();
  return useQuery({
    queryKey: ['platform', 'allergens', 'all'],
    queryFn: async () => (await fetchAllPages((page) => api.platform.listAllergens({ page, pageSize: 100 })))
      .filter((a) => a.mergedIntoId == null),
  });
}

/** An entry's names, picture and kind; for one that exists, its other names, a merge, and retiring it. */
function AllergenEditor({ editing, onClose, onSaved }: {
  editing: NonNullable<Editing>;
  onClose: () => void;
  onSaved: (allergen: AllergenResponse) => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const existing = 'allergen' in editing ? editing.allergen : null;
  const start = 'names' in editing ? editing.names : [];

  const [nameAr, setNameAr] = useState(existing?.nameAr ?? start[0] ?? '');
  const [nameEn, setNameEn] = useState(existing?.nameEn ?? '');
  const [kind, setKind] = useState<AllergenKind>(existing?.kind ?? 'Food');
  const [icon, setIcon] = useState(existing?.icon ?? 'other');
  const [aliases, setAliases] = useState(start.slice(1).join('\n'));
  const [newNames, setNewNames] = useState('');
  const [mergeFrom, setMergeFrom] = useState('');

  const live = useLiveAllergens();
  /**
   * Every save changes the catalogue and the pickers. The names nobody recognises change only where names were
   * added or removed: a merge moves names without forgetting any, and a retired entry's names still match. That
   * list is read across every nursery, so it is not read again for nothing.
   */
  const saved = (namesChanged: boolean) => (allergen: AllergenResponse) => {
    void queryClient.invalidateQueries({
      queryKey: ['platform', 'allergens'],
      predicate: (query) => namesChanged || query.queryKey[2] !== 'unmatched',
    });
    onSaved(allergen);
  };

  const save = useMutation({
    mutationFn: () => existing
      ? api.platform.updateAllergen(existing.id, { nameAr, nameEn, icon, kind })
      : api.platform.createAllergen({ nameAr, nameEn, icon, kind, aliases: splitNames(aliases) }),
    onSuccess: saved(true),
  });
  const addNames = useMutation({
    mutationFn: (id: number) => api.platform.addAllergenNames(id, splitNames(newNames)),
    onSuccess: (allergen) => { setNewNames(''); saved(true)(allergen); },
  });
  const removeName = useMutation({
    mutationFn: ({ id, aliasId }: { id: number; aliasId: number }) => api.platform.removeAllergenName(id, aliasId),
    onSuccess: saved(true),
  });
  const merge = useMutation({
    mutationFn: ({ id, fromId }: { id: number; fromId: number }) => api.platform.mergeAllergens(id, fromId),
    onSuccess: (allergen) => { setMergeFrom(''); saved(false)(allergen); },
  });
  const retire = useMutation({
    mutationFn: ({ id, back }: { id: number; back: boolean }) =>
      back ? api.platform.restoreAllergen(id) : api.platform.retireAllergen(id),
    onSuccess: saved(false),
  });

  const error = save.error ?? addNames.error ?? removeName.error ?? merge.error ?? retire.error;
  const merged = existing?.mergedIntoId != null;

  return (
    <Card
      title={existing ? t('allergens.editor.edit', { name: existing.nameAr }) : t('allergens.editor.new')}
      action={<Button variant="ghost" onClick={onClose}>{t('allergens.close')}</Button>}
    >
      <Stack>
        {error && <ErrorState error={error} />}
        {merged && <Notice tone="info">{t('allergens.merged.notice')}</Notice>}

        <form
          className="dg-stack"
          onSubmit={(event) => { event.preventDefault(); save.mutate(); }}
        >
          <Row>
            <Field label={t('allergens.field.nameAr')} required>
              {(props) => <Input {...props} value={nameAr} disabled={merged} onChange={(e) => setNameAr(e.target.value)} />}
            </Field>
            <Field label={t('allergens.field.nameEn')} required>
              {(props) => <Input {...props} dir="ltr" value={nameEn} disabled={merged} onChange={(e) => setNameEn(e.target.value)} />}
            </Field>
            <Field label={t('allergens.field.kind')}>
              {(props) => (
                <Select {...props} value={kind} disabled={merged} onChange={(e) => setKind(e.target.value as AllergenKind)}>
                  {KINDS.map((k) => <option key={k} value={k}>{t(`allergens.kind.${k}`)}</option>)}
                </Select>
              )}
            </Field>
          </Row>

          <fieldset className="dg-allergen-icons" disabled={merged}>
            <legend className="dg-field__label">{t('allergens.field.icon')}</legend>
            <p className="dg-field__hint">{t('allergens.field.icon.hint')}</p>
            <div className="dg-allergen-icons__grid">
              {Object.keys(ALLERGEN_ICONS).map((key) => (
                <button
                  key={key} type="button" className="dg-allergen-icons__choice" aria-pressed={icon === key}
                  title={key} onClick={() => setIcon(key)}
                >
                  <AllergenIcon icon={key} allergen={key} />
                </button>
              ))}
            </div>
          </fieldset>

          {!existing && (
            <Field label={t('allergens.field.aliases')} hint={t('allergens.field.aliases.hint')}>
              {(props) => <Textarea {...props} rows={3} value={aliases} onChange={(e) => setAliases(e.target.value)} />}
            </Field>
          )}

          {!merged && (
            <Row>
              <Button type="submit" busy={save.isPending} disabled={!nameAr.trim() || !nameEn.trim()}>
                {t('allergens.save')}
              </Button>
            </Row>
          )}
        </form>

        {existing && (
          <>
            <Stack tight>
              <strong>{t('allergens.names')}</strong>
              <p className="dg-field__hint">{t('allergens.names.hint')}</p>
              <ul className="dg-allergen-names">
                {existing.aliases.map((alias) => (
                  <li key={alias.id} className="dg-allergen-names__item">
                    <span>{alias.name}</span>
                    {!alias.isName && !merged && (
                      <button
                        type="button" className="dg-allergen-names__remove"
                        aria-label={t('allergens.names.remove', { name: alias.name })}
                        disabled={removeName.isPending}
                        onClick={() => removeName.mutate({ id: existing.id, aliasId: alias.id })}
                      >
                        ×
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </Stack>

            {!merged && (
              <>
                <Field label={t('allergens.names.new')} hint={t('allergens.field.aliases.hint')}>
                  {(props) => <Textarea {...props} rows={2} value={newNames} onChange={(e) => setNewNames(e.target.value)} />}
                </Field>
                <Row>
                  <Button
                    variant="secondary" busy={addNames.isPending} disabled={splitNames(newNames).length === 0}
                    onClick={() => addNames.mutate(existing.id)}
                  >
                    {t('allergens.names.add')}
                  </Button>
                </Row>

                <Stack tight>
                  <strong>{t('allergens.merge.title')}</strong>
                  <p className="dg-field__hint">{t('allergens.merge.hint', { name: existing.nameAr })}</p>
                  <Row>
                    <Field label={t('allergens.merge.pick')}>
                      {(props) => (
                        <Select {...props} value={mergeFrom} onChange={(e) => setMergeFrom(e.target.value)}>
                          <option value="">{t('allergens.pick')}</option>
                          {(live.data ?? []).filter((a) => a.id !== existing.id).map((a) => (
                            <option key={a.id} value={a.id}>{a.nameAr} — {a.nameEn}</option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <Button
                      variant="danger" busy={merge.isPending} disabled={!mergeFrom}
                      onClick={() => merge.mutate({ id: existing.id, fromId: Number(mergeFrom) })}
                    >
                      {t('allergens.merge.submit')}
                    </Button>
                  </Row>
                </Stack>

                <Stack tight>
                  <p className="dg-field__hint">{t('allergens.retire.hint')}</p>
                  <Row>
                    <Button
                      variant="secondary" busy={retire.isPending}
                      onClick={() => retire.mutate({ id: existing.id, back: existing.retiredAt != null })}
                    >
                      {existing.retiredAt ? t('allergens.restore') : t('allergens.retire')}
                    </Button>
                  </Row>
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

/**
 * The names nurseries wrote that the catalogue does not know (Q77): the text and the counts, never a nursery or a
 * child. Several rows that mean one thing are chosen together and either added to an entry or made into a new one.
 */
function UnmatchedCard({ onCreate }: { onCreate: (names: string[]) => void }) {
  const api = useApi();
  const { t, tn, formatNumber } = useI18n();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [chosen, setChosen] = useState<Map<string, UnmatchedAllergenResponse>>(new Map());
  const [target, setTarget] = useState('');

  const unmatched = useQuery({
    queryKey: ['platform', 'allergens', 'unmatched', { page }],
    queryFn: () => api.platform.unmatchedAllergens({ page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });
  // Gathering names takes them out of the list, which may then end before this page.
  useClampedPage(page, setPage, unmatched.data?.totalPages);
  const live = useLiveAllergens();

  const spellings = () => [...chosen.values()].flatMap((row) => row.spellings);
  const add = useMutation({
    mutationFn: () => api.platform.addAllergenNames(Number(target), spellings()),
    onSuccess: () => {
      setChosen(new Map());
      setTarget('');
      void queryClient.invalidateQueries({ queryKey: ['platform', 'allergens'] });
    },
  });

  const toggle = (row: UnmatchedAllergenResponse) => setChosen((current) => {
    const next = new Map(current);
    if (next.has(row.name)) next.delete(row.name);
    else next.set(row.name, row);
    return next;
  });

  const columns: Column<UnmatchedAllergenResponse>[] = [
    {
      key: 'choose', header: '',
      render: (row) => (
        <input
          type="checkbox" checked={chosen.has(row.name)} onChange={() => toggle(row)}
          aria-label={t('allergens.unmatched.choose', { name: row.name })}
        />
      ),
    },
    { key: 'name', header: t('allergens.unmatched.column.name'), render: (row) => <strong>{row.name}</strong> },
    {
      key: 'spellings', header: t('allergens.unmatched.column.spellings'),
      render: (row) => <span className="dg-muted">{row.spellings.join('، ')}</span>,
    },
    { key: 'children', header: t('allergens.unmatched.column.children'), numeric: true, render: (row) => formatNumber(row.children) },
    { key: 'nurseries', header: t('allergens.unmatched.column.nurseries'), numeric: true, render: (row) => formatNumber(row.nurseries) },
  ];

  return (
    <Card title={t('allergens.unmatched')} hint={t('allergens.unmatched.lede')}>
      <Stack>
        {add.error && <ErrorState error={add.error} />}
        {chosen.size > 0 && (
          <Row>
            <strong>{tn('allergens.unmatched.chosen', chosen.size)}</strong>
            <Field label={t('allergens.unmatched.addTo')}>
              {(props) => (
                <Select {...props} value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">{t('allergens.pick')}</option>
                  {(live.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.nameAr} — {a.nameEn}</option>)}
                </Select>
              )}
            </Field>
            <Button variant="secondary" busy={add.isPending} disabled={!target} onClick={() => add.mutate()}>
              {t('allergens.unmatched.add')}
            </Button>
            <Button onClick={() => { onCreate(spellings()); setChosen(new Map()); }}>
              {t('allergens.unmatched.create')}
            </Button>
          </Row>
        )}
        <DataTable
          columns={columns}
          rows={unmatched.data?.items}
          rowKey={(row) => row.name}
          caption={t('allergens.unmatched')}
          isLoading={unmatched.isPending}
          error={unmatched.error}
          onRetry={() => void unmatched.refetch()}
          empty={<p className="dg-card__hint">{t('allergens.unmatched.none')}</p>}
        />
        {unmatched.data && unmatched.data.totalPages > 1 && (
          <Row>
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('action.previous')}</Button>
            <span>{formatNumber(page)} / {formatNumber(unmatched.data.totalPages)}</span>
            <Button variant="ghost" disabled={page >= unmatched.data.totalPages} onClick={() => setPage(page + 1)}>
              {t('action.next')}
            </Button>
          </Row>
        )}
      </Stack>
    </Card>
  );
}
